import { z } from "zod";

const redirectSchema = z.object({ redirect: z.literal(true), url: z.url() });
// Schemes a browser runs or reads locally instead of handing to an app.
const REFUSED_SCHEMES = new Set(["javascript:", "data:", "file:", "vbscript:", "blob:"]);
// What the server appends to the client's redirect URI.
const RESPONSE_PARAMS = ["code", "state", "iss", "error", "error_description"];

function withoutResponse(value: string): string {
	const url = new URL(value);
	for (const name of RESPONSE_PARAMS) url.searchParams.delete(name);
	return url.toString();
}

// `requested` is the redirect_uri of the signed authorization request, which the server matched
// against the client's registration. An app's own scheme is followed only back to that URI.
export function oauthRedirect(data: unknown, requested?: string | null): string | null {
	const result = redirectSchema.safeParse(data);
	if (!result.success) return null;
	const url = new URL(result.data.url);
	if (url.username || url.password) return null;
	if (url.protocol === "https:") return url.toString();
	if (url.protocol === "http:" && ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname))
		return url.toString();
	if (url.protocol === "http:" || REFUSED_SCHEMES.has(url.protocol)) return null;
	if (!requested || !URL.canParse(requested)) return null;
	return withoutResponse(url.toString()) === withoutResponse(requested) ? url.toString() : null;
}

// An app's own scheme has no web origin, so the page names its scheme and host instead.
export function callbackLabel(value: string | null): string | null {
	if (!value || !URL.canParse(value)) return null;
	const url = new URL(value);
	if (url.origin !== "null") return url.origin;
	return url.host ? `${url.protocol}//${url.host}` : url.protocol;
}
